import {
  Details,
  DetailsBig,
  DetailsBigCss,
  Paragraph,
  ParagraphCss,
  h6,
} from "@/styles/typography";
import styled from "styled-components";

const Search = styled.button<{ $searchOpen?: boolean }>`
  display: none;

  @media screen and (min-width: ${({ theme }) => theme.breakpoints.xxl}) {
    background-color: ${({ theme }) => theme.colors.coreDark};
    height: 100%;
    border: none;
    cursor: pointer;
    color: ${({ theme }) => theme.colors.textHeading};
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 10px;
    font-family: "Frutiger Bold";
    font-size: 16px;
    font-style: normal;
    font-weight: 700;
    line-height: 120%;
    letter-spacing: 0.27px;
    padding: 4px 24px 0 24px;

    svg {
      margin-bottom: 5px;
    }
  }
`;

const SearchMobile = styled.button<{ $searchOpen?: boolean }>`
  background-color: ${({ theme }) => theme.colors.coreMedium};
  height: 100%;
  width: 100%;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.textHeading};
  display: flex;
  align-items: center;
  gap: 10px;
  font-family: "Frutiger Bold";
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 120%;
  letter-spacing: 0.27px;
  padding: 0 8px 0 16px;
  border: none;

  svg {
    margin-bottom: 5px;
  }

  @media screen and (min-width: ${({ theme }) => theme.breakpoints.xxl}) {
    display: none;
  }

  ${({ $searchOpen }) =>
    $searchOpen &&
    `
    width: fit-content;
  span {
      display: none;
  }
    `}
`;

const SearchContainer = styled.div<{ $searchOpen: boolean }>`
  padding: ${({ $searchOpen }) => ($searchOpen ? "10px 0" : "10px 0")};
  position: relative;
  background-color: ${({ theme }) => theme.colors.coreMedium};
  z-index: 10;

  input {
    display: ${({ $searchOpen }) => ($searchOpen ? "block" : "none")};
    background: transparent;
    height: 100%;
    width: 100%;
    border: 0;

    &:focus {
      outline: none;
    }
  }

  @media screen and (min-width: ${({ theme }) => theme.breakpoints.xxl}) {
    position: absolute;
    padding: 12px 21px 12px 70px;
    top: calc(64px + 38px);
    left: 0;
    width: 100%;
    opacity: 0;
    visibility: hidden;
    transition: all 0.2s ease-in-out;
    display: flex;
    gap: 32px;

    ${({ $searchOpen }) =>
      $searchOpen &&
      `
    opacity: 1;
    visibility: visible;
  `}
  }
`;

const FlexWrapper = styled.div`
  display: flex;
  align-items: center;
  width: 100%;

  @media screen and (min-width: ${({ theme }) => theme.breakpoints.xl}) {
    justify-content: space-between;
  }
`;

const SearchInput = styled.input`
  ${DetailsBigCss}
  color: ${({ theme }) => theme.colors.textHeading};
  outline: none;

  &::placeholder {
    color: ${({ theme }) => theme.colors.textHeading};
  }
`;

const SearchBarWrapper = styled.section<{ $active: boolean }>`
  position: absolute;
  height: fit-content;
  opacity: 1;
  width: 100%;
  left: 0;
  top: 41px;
  padding: 10px;
  display: ${({ $active }) => ($active ? "block" : "none")};
  background-color: ${({ theme }) => theme.colors.coreDark};
  overflow-y: auto;
  max-height: calc(100vh - 250px);
  z-index: 100;

  @media screen and (min-width: ${({ theme }) => theme.breakpoints.xl}) {
    padding: 12px 70px;
  }
`;

const SearchResults = styled.ul<{ $active: boolean; $data: any }>`
  ${ParagraphCss}
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 16px;
  z-index: 100;
  position: relative;

  @media screen and (min-width: ${({ theme }) => theme.breakpoints.xl}) {
    gap: 24px;
  }
`;

const SearchItem = styled.li`
  padding: 16px;
  background-color: ${({ theme }) => theme.colors.coreLight};
  border-radius: 4px;
  max-width: 850px;

  @media screen and (min-width: ${({ theme }) => theme.breakpoints.xxl}) {
    padding: 24px;
  }
`;

const ResultCard = styled.a`
  color: ${({ theme }) => theme.colors.primaryPurple};
  display: flex;
  flex-direction: column;
  gap: 16px;
  text-decoration: none;

  &:hover {
    span {
      text-decoration-color: ${({ theme }) => theme.colors.primaryPurple};
    }
  }
`;

const ResultTitleWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 24px;

  @media screen and (min-width: ${({ theme }) => theme.breakpoints.xxl}) {
    gap: 40px;
  }
`;

const ResultTitle = styled(h6)`
  text-decoration: underline;
  text-decoration-color: transparent;
  transition: 0.3s;
`;

const ResultText = styled(Paragraph)`
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2; /* number of lines to show */
  -webkit-box-orient: vertical;
  white-space: normal;
  word-wrap: break-word;
  max-width: 820px;
`;

const ResultInfo = styled.div`
  display: none;

  @media screen and (min-width: ${({ theme }) => theme.breakpoints.xxl}) {
    display: flex;
    align-items: center;
    gap: 16px;
  }
`;

const ResultDate = styled(Details)``;

const Overlay = styled.div<{ $searchOpen: boolean }>`
  display: none;

  @media screen and (min-width: ${({ theme }) => theme.breakpoints.xxl}) {
    position: fixed;
    top: 190px;
    left: 0;
    width: 100%;
    height: 100%;
    display: ${({ $searchOpen }) => ($searchOpen ? "block" : "none")};
    background-color: rgba(0, 0, 0, 0.3);
    z-index: 80;
  }
`;

const DetailsWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  max-width: 850px;
`;

const DetailsText = styled(DetailsBig)`
  color: ${({ theme }) => theme.colors.textHeading};
  padding: 8px 0;

  @media screen and (min-width: ${({ theme }) => theme.breakpoints.xxl}) {
    padding: 16px 0;
  }
`;

const S = {
  SearchContainer,
  SearchMobile,
  Search,
  FlexWrapper,
  SearchInput,
  SearchResults,
  SearchItem,
  SearchBarWrapper,
  ResultCard,
  ResultTitle,
  ResultText,
  ResultInfo,
  ResultDate,
  Overlay,
  DetailsWrapper,
  DetailsText,
  ResultTitleWrapper,
};

export default S;
